import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';


import CashHandoverFromSalesReportHeader from './CashHandoverFromSalesReportHeader';
import './CashHandoverFromSalesReport.scss';
// Dummy restaurants / recipients — replace with API/Redux data
const RESTAURANTS = [
    { label: 'Chenthoor Restaurant', value: 'Chenthoor Restaurant' },
    { label: 'Chenthoor Restaurant - 2', value: 'Chenthoor Restaurant - 2' },
];

const HANDOVER_TO = 'SADAK THAMBI KAKA';
const NOTE_VALUE = 500;

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const pad = (n) => String(n).padStart(2, '0');

// dd/mm/yy, same as the reference sheet
const formatDate = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;

const formatNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function generateDateRange(from, to) {
    const dates = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (cur <= end) {
        dates.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

// Dummy daily handover — replace with API data: { date, notes, remarks, to }
function seedNotes(date, index) {
    const seed = date.getDate() * 37 + (date.getMonth() + 1) * 11 + index * 7;
    return 2 + (seed % 38);
}

// Derived columns, matching the sheet: Total = notes * denomination,
// Total Amount Received = running total of Total.
function buildRows(dates) {
    let received = 0;
    return dates.map((date, i) => {
        const notes = seedNotes(date, i);
        const total = round2(notes * NOTE_VALUE);
        received = round2(received + total);
        return {
            id: i,
            date,
            denomination: `${NOTE_VALUE}*${notes}`,
            total,
            received,
            remarks: '',
            to: HANDOVER_TO,
        };
    });
}

const COLUMN_LABELS = ['DATE', 'Denomination', 'TOTAL', 'TOTAL AMOUNT RECEIVED', 'REMARKS', 'TO'];



class CashHandoverFromSalesReport extends Component {
    state = {
        Restaurant: RESTAURANTS[0].value,
        fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        toDate: new Date(),
        tableData: [],
        searched: false,
        btnLoading: null, // key of the action currently running: search | showAll | clear | pdf | excel
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    // Same loader as the Daily Sales Report (full-page .page-loader overlay) — shown until the action
    // completes; further clicks are ignored meanwhile.
    runWithLoader = (key, action) => {
        if (this.state.btnLoading) return;
        this.setState({ btnLoading: key }, () => {
            setTimeout(() => {
                try {
                    action();
                } catch (e) {
                    this.growl && this.growl.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong. Please try again.' });
                } finally {
                    if (!this.unmounted) this.setState({ btnLoading: null });
                }
            }, 300);
        });
    };

    handleSearch = () => this.runWithLoader('search', this.handleSearchAction);
    handleShowAll = () => this.runWithLoader('showAll', this.handleShowAllAction);
    handleClear = () => this.runWithLoader('clear', this.handleClearAction);
    handleExportExcel = () => this.runWithLoader('excel', this.handleExportExcelAction);
    handleExportPdf = () => this.runWithLoader('pdf', this.handleExportPdfAction);

    handleSearchAction = () => {
        const { fromDate, toDate, Restaurant } = this.state;
        if (!Restaurant) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select Restaurant' });
            return;
        }
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        this.setState({ tableData: buildRows(generateDateRange(fromDate, toDate)), searched: true });
    };

    handleShowAllAction = () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        this.setState({ fromDate: from, toDate: now, tableData: buildRows(generateDateRange(from, now)), searched: true });
    };

    handleClearAction = () => {
        const now = new Date();
        this.setState({ Restaurant: RESTAURANTS[0].value, fromDate: new Date(now.getFullYear(), now.getMonth(), 1), toDate: now, tableData: [], searched: false });
    };

    getTotal = () => round2(this.state.tableData.reduce((a, r) => a + Number(r.total || 0), 0));

    buildExportMatrix = () => {
        const body = this.state.tableData.map(r => [formatDate(r.date), r.denomination, r.total, r.received, r.remarks, r.to]);
        const last = this.state.tableData[this.state.tableData.length - 1];
        const total = ['TOTAL', '', this.getTotal(), last ? last.received : 0, '', ''];
        return { body, total };
    };

    reportTitle = () => {
        const { Restaurant, fromDate, toDate } = this.state;
        return `${String(Restaurant).toUpperCase()} - CASH HANDOVER FROM SALES (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    };

    handleExportExcelAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const ws = XLSX.utils.aoa_to_sheet([[this.reportTitle()], COLUMN_LABELS, ...body, total]);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMN_LABELS.length - 1 } }];
        ws['!cols'] = COLUMN_LABELS.map(() => ({ wch: 22 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cash Handover');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Cash_Handover_From_Sales_Report.xlsx');
    };

    handleExportPdfAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const fmt = (row) => row.map((v, i) => ((i === 2 || i === 3) && v !== '' ? formatNum(v) : v));
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(12);
        doc.text(this.reportTitle(), 14, 10);
        doc.autoTable({
            head: [COLUMN_LABELS],
            body: [...body.map(fmt), fmt(total)],
            startY: 15,
            styles: { fontSize: 8, cellPadding: 1.5, halign: 'center' },
            columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
            headStyles: { fillColor: [244, 176, 176], textColor: 0, halign: 'center' },
            didDrawCell: (d) => {
                // vertical separators: left edge of every cell, plus the right edge of the last column
                doc.setDrawColor(0);
                doc.setLineWidth(0.2);
                doc.line(d.cell.x, d.cell.y, d.cell.x, d.cell.y + d.cell.height);
                if (d.column.index === COLUMN_LABELS.length - 1) {
                    const xr = d.cell.x + d.cell.width;
                    doc.line(xr, d.cell.y, xr, d.cell.y + d.cell.height);
                }
            },
            didParseCell: (d) => {
                if (d.section === 'body' && d.row.index === body.length) d.cell.styles.fontStyle = 'bold';
            },
        });
        doc.save('Cash_Handover_From_Sales_Report.pdf');
    };

    numTemplate = (field) => (row) => formatNum(row[field]);

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, fromDate, toDate, searched, Restaurant, btnLoading } = this.state;
        const busy = btnLoading !== null;
        const right = { textAlign: 'right' };
        const center = { textAlign: 'center' };
        const last = tableData[tableData.length - 1];

        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column header={this.reportTitle()} colSpan={6} style={{ textAlign: 'center', background: '#f4b0b0', color: '#000' }} />
                </Row>
                <Row>
                    <Column header="DATE" style={{ minWidth: '110px', ...center }} />
                    <Column header="Denomination" style={{ minWidth: '130px', ...center }} />
                    <Column header="TOTAL" style={{ minWidth: '120px', ...center }} />
                    <Column header="TOTAL AMOUNT RECEIVED" style={{ minWidth: '160px', ...center }} />
                    <Column header="REMARKS" style={{ minWidth: '140px', ...center }} />
                    <Column header="TO" style={{ minWidth: '180px', ...center }} />
                </Row>
            </ColumnGroup>
        );

        const footerGroup = (
            <ColumnGroup>
                <Row>
                    <Column footer="TOTAL" colSpan={2} footerStyle={center} />
                    <Column footer={formatNum(searched ? this.getTotal() : 0)} footerStyle={right} />
                    <Column footer={formatNum(last ? last.received : 0)} footerStyle={right} />
                    <Column footer="" />
                    <Column footer="" />
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <CashHandoverFromSalesReportHeader />

                        <div className="p-grid p-align-end">
                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={RESTAURANTS}
                                    value={Restaurant}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar placeholder="From Date" value={fromDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ fromDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar placeholder="To Date" value={toDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ toDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-5 p-lg-5 p-d-flex p-ai-end p-gap-2">
                                <Button label="Search" icon="pi pi-search" className="p-button-primary" onClick={this.handleSearch} disabled={busy} />
                                <Button label="Show All" icon="pi pi-list" className="p-button-secondary p-ml-2" onClick={this.handleShowAll} disabled={busy} />
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary p-ml-2" onClick={this.handleClear} disabled={busy} />
                            </div>

                            <div className="p-col-12 p-d-flex p-jc-end p-gap-2 p-mt-1">
                                <Button label="Export as Excel" icon="pi pi-file-excel" className="p-button-warning" onClick={this.handleExportExcel} disabled={!searched || busy} />
                                <Button label="Export as PDF" icon="pi pi-file-pdf" className="p-button-danger p-ml-2" onClick={this.handleExportPdf} disabled={!searched || busy} />
                            </div>
                        </div>

                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    footerColumnGroup={footerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    style={{ width: '100%' }}
                                    emptyMessage="No records found"
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column field="date" body={(r) => formatDate(r.date)} style={{ minWidth: '110px', ...center }} />
                                    <Column field="denomination" style={{ minWidth: '130px', ...center, fontWeight: 'bold' }} />
                                    <Column field="total" body={this.numTemplate('total')} style={{ minWidth: '120px', ...right, fontWeight: 'bold' }} />
                                    <Column field="received" body={this.numTemplate('received')} style={{ minWidth: '160px', ...right, fontWeight: 'bold' }} />
                                    <Column field="remarks" style={{ minWidth: '140px', ...center }} />
                                    <Column field="to" style={{ minWidth: '180px', ...center }} />
                                </DataTable>
                            </div>
                        )}

                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(CashHandoverFromSalesReport);
